import { ReactNode, useEffect, useState } from 'react';
import { User } from '../../../../clients/quipli-service/user/user';
import { Typography } from '../../../../components/typography/typography';
import { Dropdown } from '../../../../components/dropdown/dropdown';
import { Button } from '../../../../components/form/button';
import Icon from '../../../../components/icons/icon';
import { ConfirmModal } from '../../../../components/modal/confirm-modal';
import { Card } from '../../../../components/card/card';
import { UserRepresentativeForm } from '../../components/user-representative-form';
import { formatPhoneNumber } from '../../../../util/util';
import { useRepresentativeApi } from '../../../../clients/quipli-service/representatives/representative-api';
import { Representative } from '../../../../clients/quipli-service/representatives/representative';
import { Spinner } from '../../../../components/spinner/spinner';
import { Dictionary, get, groupBy } from 'lodash';

export const Representatives = ({ user }: { user: User }) => {
  const { getRepresentatives, deleteRepresentative } = useRepresentativeApi();
  const [data, setData] = useState<Dictionary<Representative[]>>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modal, setModal] = useState<ReactNode>();

  const fetchRepresentatives = async () => {
    setIsLoading(true);
    const representatives = await getRepresentatives(user.id);
    setData(groupBy(representatives, (item) => get(item, 'role.name', 'Others')));
    setIsLoading(false);
  };

  const reloadFromModal = async () => {
    await fetchRepresentatives();
    closeModal();
  };

  const closeModal = () => setModal(undefined);

  useEffect(() => {
    fetchRepresentatives();
  }, []);

  return (
    <div className="tab-section">
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          {!data || Object.keys(data).length === 0 ? (
            <Card className="no_item_page">
              <Icon icon="Location02" size="large" />
              <Typography type="text-sm" weight="medium" className="property text-grey-500">
                Looks like you haven't added representatives yet.
                <br></br> Click “Add Representative” to get started
              </Typography>
              <button
                className="save-button"
                onClick={() =>
                  setModal(
                    <UserRepresentativeForm
                      isOpen={true}
                      onSubmit={reloadFromModal}
                      onClose={closeModal}
                      userId={user.id}
                    />,
                  )
                }
              >
                Add Representative
              </button>
            </Card>
          ) : (
            <Card className="rounded-2xl bg-white p-4 flex flex-column gap-4 w-fit min-w-[50%]">
              {Object.keys(data).map((role, index) => {
                return (
                  <div key={`role-${index}`}>
                    <Typography type="text-lg" weight="semi-bold">
                      {role || 'Others'}
                    </Typography>
                    <div className="pt-2 grid grid-cols-2 justify-between gap-2 items-start">
                      <div>
                        {data[role].map((item, index) => (
                          <RepresentativeItem
                            key={`address-item-${index}`}
                            object={item}
                            onClickEdit={() =>
                              setModal(
                                <UserRepresentativeForm
                                  isOpen={true}
                                  onSubmit={reloadFromModal}
                                  onClose={closeModal}
                                  userId={user.id}
                                  representative={item}
                                />,
                              )
                            }
                            onClickDelete={() =>
                              setModal(
                                <ConfirmModal
                                  isOpen={true}
                                  close={closeModal}
                                  onConfirm={async () => {
                                    await deleteRepresentative(item);
                                    await reloadFromModal();
                                  }}
                                  headerIcon={
                                    <div className="flex justify-center">
                                      <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
                                    </div>
                                  }
                                  prompt="Remove item?"
                                  additionalText="This cannot be undone"
                                  noLabel="Cancel"
                                  yesLabel="Remove"
                                />,
                              )
                            }
                          ></RepresentativeItem>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
              <button
                className="save-button"
                onClick={() =>
                  setModal(
                    <UserRepresentativeForm
                      isOpen={true}
                      onSubmit={reloadFromModal}
                      onClose={closeModal}
                      userId={user.id}
                    />,
                  )
                }
              >
                Add Representative
              </button>
            </Card>
          )}
        </>
      )}

      {/* Add, edit and delete modals */}
      {modal}
    </div>
  );
};

const RepresentativeItem = ({
  object,
  onClickDelete,
  onClickEdit,
}: {
  object: Representative;
  onClickDelete: () => void;
  onClickEdit: () => void;
}) => {
  return (
    <>
      <Card className="rounded-2xl bg-white p-4 flex flex-column gap-4 w-fit min-w-[50%]">
        <div className="title">
          <div className="flex items-center gap-2">
            <Typography type="text-md" weight="semi-bold">
              {object.name}
            </Typography>
            <Icon icon="AddCustomer" size="medium" />
          </div>

          <div>
            <Dropdown
              trigger={() => (
                <Button className="button-tertiary">
                  <Icon icon="DotsVertical" stroke="var(--grey-800)" size="small" />
                </Button>
              )}
            >
              <div>
                <div className="flex gap-2" onClick={onClickEdit} data-cy="disable_customer">
                  <Typography type="text-sm" className="text-quipli-black-800">
                    Edit Representative
                  </Typography>
                </div>
              </div>
              <div>
                <div className="flex gap-2" onClick={onClickDelete} data-cy="delete_customer">
                  <Typography type="text-sm" className="text-error-500">
                    Delete Representative
                  </Typography>
                </div>
              </div>
            </Dropdown>
          </div>
        </div>
        <div className="h-[1px] w-full bg-grey-300"></div>
        {object.title && (
          <span className="quipli-v2 text-sm regular text-[var(--quipli-black-800)]">{object.title}</span>
        )}
        <span className="quipli-v2 text-sm regular text-[var(--quipli-black-800)]">{object.email}</span>
        <span className="quipli-v2 text-sm regular text-[var(--quipli-black-800)]">
          {formatPhoneNumber(object.phone)}
        </span>
      </Card>
    </>
  );
};
